import { useState } from 'react'
import { Network, CheckCircle2, AlertCircle, Copy, Check } from 'lucide-react'
import Spinner from '../Spinner'
import type { Project } from '../../data/projectTypes'
import { buildReplayQaAgentInstructions } from '../../lib/reverseProxyInstructions'
import {
  appUnavailableDescription,
  REVERSE_PROXY_PUBLIC_ADDRESS_PENDING_ERROR,
} from '../../lib/reverseProxyStatusCopy'
import { useGetReverseProxyStatusQuery } from '../../services/projectDataApi'

// The reverse-proxy setup, rendered INSIDE the project chat (not just the Overview) for projects
// whose target is only reachable through a tunnel. It polls the control plane, shows the
// self-contained copy-paste runbook while frpc is connecting, and auto-detects the moment it
// connects — which is also what opens the server-side start gate, so QA cannot start until the proxy
// is connected. Compact styling (chat.css .rp-card*) suited to the narrow chat column.

function CopyInstructionsButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="rp-card__copy"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      aria-label="Copy replayqa cli setup instructions"
      data-testid="chat-reverse-proxy-copy"
      data-analytics-event="chat-reverse-proxy-copy"
    >
      {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
      {copied ? 'Copied' : 'Copy setup instructions'}
    </button>
  )
}

// `autoStart` (default true) matches the in-project chat, where the project is already active so QA
// begins the moment frpc connects. The onboarding chat passes autoStart=false: the project is held
// paused until the user presses Start QA, so the copy points at that button instead.
export default function ReverseProxyChatCard({ project, autoStart = true }: { project: Project; autoStart?: boolean }) {
  const statusQuery = useGetReverseProxyStatusQuery(project.id, { pollingInterval: 5_000, skipPollingIfUnfocused: true })
  const status = statusQuery.data
  const ready = status?.ready === true

  const targetUrl = status?.target_url ?? project.target_url ?? ''
  const setupInstructions = buildReplayQaAgentInstructions({
    projectId: project.id,
    qaUrl: window.location.origin,
    targetUrl,
  })

  if (ready) {
    return (
      <div className="rp-card rp-card--ready" data-testid="chat-reverse-proxy" data-status="ready">
        <CheckCircle2 size={15} className="rp-card__icon rp-card__icon--ok" aria-hidden />
        <div className="rp-card__main">
          <span className="rp-card__title">Local computer connected</span>
          <span className="rp-card__text">
            {autoStart
              ? <>QA can now reach your app and has started.</>
              : <>QA can now reach your app. Press <strong>Start QA</strong> below to begin.</>}
          </span>
        </div>
      </div>
    )
  }

  // Control plane not available in this environment — be honest about the block.
  if (status && status.configured === false) {
    return (
      <div className="rp-card rp-card--warn" data-testid="chat-reverse-proxy" data-status="unconfigured">
        <div className="rp-card__head">
          <Network size={15} className="rp-card__icon" aria-hidden />
          <span className="rp-card__title">Connect your local computer</span>
        </div>
        <p className="rp-card__text">
          QA needs your local computer connected to reach <code>{targetUrl}</code>. This feature is not
          available here yet — contact an administrator.
        </p>
      </div>
    )
  }

  if (status && status.state === 'error') {
    return (
      <div className="rp-card rp-card--warn" data-testid="chat-reverse-proxy" data-status="error">
        <div className="rp-card__head">
          <AlertCircle size={15} className="rp-card__icon" aria-hidden />
          <span className="rp-card__title">Could not connect your local computer</span>
        </div>
        <p className="rp-card__text">
          {status.error === REVERSE_PROXY_PUBLIC_ADDRESS_PENDING_ERROR
            ? appUnavailableDescription(targetUrl)
            : 'Could not connect your local computer. Retrying…'}
        </p>
        <div className="rp-card__actions">
          <CopyInstructionsButton text={setupInstructions} />
        </div>
      </div>
    )
  }

  const tunnel = status?.tunnel ?? null

  // Provisioning — connection details not ready yet.
  if (!tunnel) {
    return (
      <div className="rp-card" data-testid="chat-reverse-proxy" data-status="provisioning">
        <div className="rp-card__head">
          <Spinner size={15} className="rp-card__icon" />
          <span className="rp-card__title">Preparing local computer connection…</span>
        </div>
        <p className="rp-card__text">
          Getting things ready for your app. Setup instructions will appear here soon.
          <strong> QA can't start until your local computer connects.</strong>
        </p>
      </div>
    )
  }

  // Provisioned, waiting for the managed CLI — show the same npx runbook as the Overview and API.
  return (
    <div className="rp-card" data-testid="chat-reverse-proxy" data-status="awaiting-frpc">
      <div className="rp-card__head">
        <Network size={15} className="rp-card__icon" aria-hidden />
        <span className="rp-card__title">Connect your local computer using the replayqa cli</span>
      </div>
      <p className="rp-card__text">
        Run the copied setup instructions on the computer that can open <code>{targetUrl}</code>.
        {autoStart
          ? <strong> QA starts automatically when your local computer connects.</strong>
          : <strong> Once your local computer connects, the Start QA button below will enable.</strong>}
      </p>
      <div className="rp-card__actions">
        <CopyInstructionsButton
          text={setupInstructions}
        />
      </div>
      <div className="rp-card__waiting">
        <Spinner size={13} className="rp-card__icon" />
        <span>Waiting for your local computer to connect…</span>
      </div>
    </div>
  )
}
